import type { Translations } from '../../i18n/translations'
import { AdSlot } from '../../components/AdSlot'
import type { Locale } from '../../i18n/translations'
import { TermsAndConditionsPage } from './TermsAndConditionsPage'

type InfoPage = 'home' | 'howto' | 'about' | 'contact' | 'terms'

const CONTACT_ISSUES_URL = 'https://github.com/guiklein-ai/InvestFacil/issues'

interface PublicPageContentProps {
  t: Translations
  locale: Locale
  isDarkMode: boolean
  infoPage: InfoPage
  setInfoPage: (page: InfoPage) => void
}

export function PublicPageContent({
  t,
  locale,
  isDarkMode,
  infoPage,
  setInfoPage
}: PublicPageContentProps): JSX.Element {
  const templateTheme = isDarkMode ? 'dark' : 'light'
  const heroCluster = isDarkMode
    ? '/template-assets/dark/hero-device-cluster-blended.png'
    : '/template-assets/light/hero-device-cluster.png'
  const heroCardTop = `/template-assets/${templateTheme}/hero-card-top.png`
  const heroCardBottom = `/template-assets/${templateTheme}/hero-card-bottom.png`
  const heroCoins = `/template-assets/${templateTheme}/hero-coins.png`
  const trustIcons = [1, 2, 3, 4].map((index) =>
    index === 2
      ? `/template-assets/${templateTheme}/trust-2-transparent.svg`
      : `/template-assets/${templateTheme}/trust-${index}-transparent.png`
  )
  const stepIcons = [
    `/template-assets/${templateTheme}/step-1.png`,
    '/template-assets/money-house.png',
    `/template-assets/${templateTheme}/step-3.png`,
    isDarkMode
      ? '/template-assets/dark/step-4-complete-no-glow.png'
      : '/template-assets/light/step-4.png'
  ]
  const stepArrows = [1, 2, 3].map(
    (index) => `/template-assets/${templateTheme}/step-arrow-${index}.png`
  )
  const disclaimerShield = `/template-assets/${templateTheme}/disclaimer-shield-transparent.png`

  function renderHomeContent(): JSX.Element {
    return (
      <>
        <div className="home-hero-grid">
          <div className="home-hero-copy">
            <p className="marketing-kicker">{t.marketingKicker}</p>
            <h2 className="home-hero-title">
              {t.marketingTransformTitle}.<span>{t.marketingTransformP2}</span>
            </h2>
            <p className="marketing-copy">{t.marketingTransformLead}</p>
            <p className="marketing-copy">{t.marketingTransformP3}</p>
            <div className="marketing-cta-row">
              <button
                type="button"
                className="marketing-cta-button"
                onClick={() => setInfoPage('howto')}
              >
                {t.publicHowTo}
              </button>
              <button
                type="button"
                className="marketing-cta-button is-secondary"
                onClick={() => setInfoPage('contact')}
              >
                {t.publicContact}
              </button>
            </div>
          </div>

          <div className="home-hero-visual" aria-hidden="true">
            <img className="home-hero-cluster" src={heroCluster} alt="" />
            <img className="home-hero-card-top" src={heroCardTop} alt="" />
            <img className="home-hero-card-bottom" src={heroCardBottom} alt="" />
            <img className="home-hero-coins" src={heroCoins} alt="" />
          </div>
        </div>

        <div
          className="marketing-trust-grid home-trust-grid"
          aria-label={t.marketingHighlightsTitle}
        >
          <article className="marketing-trust-card">
            <img src={trustIcons[0]} alt="" aria-hidden="true" className="marketing-trust-icon" />
            <h3>{t.marketingStep1Title.replace(/^\d+\.\s*/, '')}</h3>
            <p>{t.marketingHighlight1}</p>
          </article>
          <article className="marketing-trust-card">
            <img src={trustIcons[1]} alt="" aria-hidden="true" className="marketing-trust-icon" />
            <h3>{t.marketingStep2Title.replace(/^\d+\.\s*/, '')}</h3>
            <p>{t.marketingHighlight2}</p>
          </article>
          <article className="marketing-trust-card">
            <img src={trustIcons[2]} alt="" aria-hidden="true" className="marketing-trust-icon" />
            <h3>{t.marketingStep3Title.replace(/^\d+\.\s*/, '')}</h3>
            <p>{t.marketingHighlight3}</p>
          </article>
          <article className="marketing-trust-card">
            <img src={trustIcons[3]} alt="" aria-hidden="true" className="marketing-trust-icon" />
            <h3>{t.marketingStep4Title.replace(/^\d+\.\s*/, '')}</h3>
            <p>{t.marketingTransformP3}</p>
          </article>
        </div>

        <div className="home-steps-panel">
          <h3 className="marketing-title">{t.marketingHowTitle}</h3>
          <div className="home-steps-row">
            <article className="home-step-card">
              <img src={stepIcons[0]} alt="" aria-hidden="true" className="home-step-icon" />
              <h4>{t.marketingStep1Title}</h4>
              <p>{t.marketingStep1Body}</p>
            </article>
            <img src={stepArrows[0]} alt="" aria-hidden="true" className="home-step-arrow" />
            <article className="home-step-card">
              <img src={stepIcons[1]} alt="" aria-hidden="true" className="home-step-icon" />
              <h4>{t.marketingStep2Title}</h4>
              <p>{t.marketingStep2Body}</p>
            </article>
            <img src={stepArrows[1]} alt="" aria-hidden="true" className="home-step-arrow" />
            <article className="home-step-card">
              <img src={stepIcons[2]} alt="" aria-hidden="true" className="home-step-icon" />
              <h4>{t.marketingStep3Title}</h4>
              <p>{t.marketingStep3Body}</p>
            </article>
            <img src={stepArrows[2]} alt="" aria-hidden="true" className="home-step-arrow" />
            <article className="home-step-card">
              <img
                src={stepIcons[3]}
                alt=""
                aria-hidden="true"
                className={`home-step-icon ${isDarkMode ? '' : 'home-step-icon-gradient'}`}
              />
              <h4>{t.marketingStep4Title}</h4>
              <p>{t.marketingStep4Body}</p>
            </article>
          </div>
        </div>

        <div className="home-disclaimer-card">
          <img src={disclaimerShield} alt="" aria-hidden="true" className="marketing-trust-icon" />
          <div>
            <h3>{t.aboutNoIntermediationTitle}</h3>
            <p>{t.aboutNoIntermediationP1}</p>
          </div>
          <button
            type="button"
            className="marketing-cta-button is-secondary"
            onClick={() => setInfoPage('contact')}
          >
            Saiba mais
          </button>
        </div>

        <footer className="home-footer-brand">
          <span className="home-footer-logo-crop">
            <img src={`/template-assets/${templateTheme}/logo-transparent.png`} alt={t.appName} />
          </span>
          <div>
            <p className="home-footer-slogan">
              {t.marketingFooterLead} <strong>{t.marketingFooterEmphasis}</strong>
            </p>
            <p className="home-footer-tagline">{t.marketingFooterTagline}</p>
          </div>
        </footer>
      </>
    )
  }

  function renderAboutContent(): JSX.Element {
    return (
      <>
        <p className="marketing-kicker">{t.aboutKicker}</p>
        <h2 className="marketing-title">{t.aboutTitle}</h2>
        <p className="marketing-copy">{t.aboutLead}</p>

        <div className="marketing-section">
          <h3>{t.aboutMissionTitle}</h3>
          <p className="marketing-copy">{t.aboutMissionP1}</p>
          <p className="marketing-copy">{t.aboutMissionP2}</p>
        </div>

        <div className="marketing-section">
          <h3>{t.aboutNoIntermediationTitle}</h3>
          <p className="marketing-copy">{t.aboutNoIntermediationP1}</p>
          <p className="marketing-copy">{t.aboutNoIntermediationP2}</p>
        </div>

        <div className="marketing-section">
          <p className="marketing-disclaimer">{t.aboutDisclaimer}</p>
        </div>
      </>
    )
  }

  function renderHowToContent(): JSX.Element {
    return (
      <>
        <p className="marketing-kicker">{t.howToKicker}</p>
        <h2 className="marketing-title">{t.howToTitle}</h2>
        <p className="marketing-copy">{t.howToLead}</p>

        <div className="marketing-section">
          <h3>{t.howToFreeRegistrationTitle}</h3>
          <p className="marketing-copy">{t.howToFreeRegistrationBody}</p>
        </div>

        <div className="marketing-section">
          <h3>{t.howToFlowTitle}</h3>
          <ol className="marketing-steps">
            <li>
              <strong>{t.howToFlow1Title}</strong>
              <p>{t.howToFlow1Body}</p>
            </li>
            <li>
              <strong>{t.howToFlow2Title}</strong>
              <p>{t.howToFlow2Body}</p>
            </li>
            <li>
              <strong>{t.howToFlow3Title}</strong>
              <p>{t.howToFlow3Body}</p>
            </li>
            <li>
              <strong>{t.howToFlow4Title}</strong>
              <p>{t.howToFlow4Body}</p>
            </li>
          </ol>
        </div>

        <div className="marketing-section">
          <h3>{t.howToReminderTitle}</h3>
          <p className="marketing-disclaimer">{t.howToReminderBody}</p>
        </div>
      </>
    )
  }

  function renderContactContent(): JSX.Element {
    return (
      <>
        <p className="marketing-kicker">{t.contactKicker}</p>
        <h2 className="marketing-title">{t.contactTitle}</h2>
        <p className="marketing-copy">{t.contactLead}</p>

        <div className="marketing-section">
          <h3>{t.contactPrimaryChannelTitle}</h3>
          <p className="marketing-copy">{t.contactPrimaryChannelBody}</p>
          <a className="marketing-cta" href={CONTACT_ISSUES_URL} target="_blank" rel="noreferrer">
            {t.contactPrimaryCta}
          </a>
        </div>

        <div className="marketing-section">
          <h3>{t.contactWhatToSendTitle}</h3>
          <ul className="marketing-highlights">
            <li>{t.contactItem1}</li>
            <li>{t.contactItem2}</li>
            <li>{t.contactItem3}</li>
          </ul>
        </div>
      </>
    )
  }

  function renderMarketingContent(): JSX.Element {
    if (infoPage === 'terms') return <TermsAndConditionsPage locale={locale} t={t} />
    if (infoPage === 'howto') return renderHowToContent()
    if (infoPage === 'about') return renderAboutContent()
    if (infoPage === 'contact') return renderContactContent()
    return renderHomeContent()
  }

  return (
    <section
      className={`marketing-panel ${infoPage === 'home' ? 'home-template-panel' : ''}`}
      aria-label={t.marketingAriaLabel}
    >
      {renderMarketingContent()}

      {infoPage !== 'home' && (
        <div className="marketing-sponsored">
          <AdSlot
            fallback={
              <div className="ad-banner-placeholder">This frame contains sponsored content.</div>
            }
          />
        </div>
      )}
    </section>
  )
}
